import React, { FC, useEffect, useState } from 'react';
import { SectionWrapper } from '../SectionWrapper';
import { useTranslation } from 'react-i18next';
import { IModalWindow, ModalWindow } from '../modal/ModalWindow';
import { USER_INFORMATION } from '../../constants/users/user-information';
import { useIdentity } from '../../store/identyti/identity-context';
import { useMerchants } from '../../store/merchnts/merchants-context';
import { FormInput } from '../merchants/FormInput';
import {
	convertToInitial,
	FORM_FIELDS, FORM_FIELDS_NAMES, getFormattedInitialUserData, getFormattedUsersData,
	initialValues,
	IUserDetails, ROLE, ROLES, userMerchantDetailsSchema,
	userSuperAdminDetailsSchema,
} from '../../constants/users/userModalUtils';
import { IAuthState } from '../../redux/AuthRedux';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from '../../../setup';
import { checkRoles, CLAIMS } from '../../utils/roles-helper';
import { useFormik } from 'formik';
import { RadioInput } from '../common/RadioInput';
import { isEqual } from 'lodash';
import { errorHandler } from '../../utils/newApplicationUtils';
import { fetchUsersList, setUserDetails } from '../../store/identyti/actions/users';
import { useLocation } from 'react-router';
import { Input } from '../developers/page-components/Input';
import { UserModel } from '../../models/UserModel';

export const UserDetailsInformation: FC = () => {
	const authUser: IAuthState = useSelector<RootState>(({ auth }) => auth, shallowEqual);
	const isAdmin = checkRoles(authUser, CLAIMS.SUPER_ADMIN);
	const { t } = useTranslation();
	const { search } = useLocation();

	const searchParams = new URLSearchParams(search);
	const userEmail = searchParams.get('email')?.replace(' ', '+');

	const { identityState: { usersList }, dispatch } = useIdentity();
	const { merchantState: { listOfMerchants: merchantList }} = useMerchants();
	const user = usersList[0];

	const [userRole, setUserRole] = useState(user?.group ? user.group[0] : ROLE.MERCHANT_ADMIN);
	const [isEditMode, setIsEditMode] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [modalState, setModalState] = useState<IModalWindow>({
	   isOpen: false
	 });

	const closeModal = (): void => setModalState(prev => ({ ...prev, isOpen: false }));

		const formik = useFormik<IUserDetails>({
    initialValues,
    validationSchema: isAdmin ? userSuperAdminDetailsSchema : userMerchantDetailsSchema,
    onSubmit: () => void 0
	});

	useEffect(() => {
		formik.setValues(convertToInitial(user));
	}, [user]);


	const emailAdditionalClasses = 'disabled';

	const additionalClasses = '';

	const INPUTS = USER_INFORMATION({ isDisabled: true, emailAdditionalClasses, additionalClasses, merchantList });

	const fields = isAdmin
		? INPUTS
		: INPUTS.filter(input => input.key !== FORM_FIELDS.MERCHANT_ID);

	const handleSaveClick = async (): Promise<void> => {
		formik.handleSubmit();
		if (formik.isValid) {
			try {
				const searchParams = {
					filterBy: 'email',
					query: userEmail,
				};

				const initialUserData = getFormattedInitialUserData(user);
				const changedData = getFormattedUsersData(formik.values);
				changedData.group = userRole;
				changedData.merchantId = user?.merchantId;
				if (!isEqual(changedData, initialUserData)) {
					setIsLoading(true);
					await setUserDetails(formik, userRole, isAdmin);
					await fetchUsersList({ dispatch, searchParams });
					setIsLoading(false);
				}
				setIsEditMode(!isEditMode);
			} catch (e) {
				console.log(e);
				errorHandler({ e, formik });
				setIsLoading(false);
			}
		}
	};

	const handleCancelClick = (): void => {
		const initialUserData = getFormattedInitialUserData(user);
		const changedData = getFormattedUsersData(formik.values);
		changedData.group = userRole;
		changedData.merchantId = user?.merchantId;
		const initialValues = convertToInitial(user);
		if (!isEqual(changedData, initialUserData)) {
			setModalState({
        isOpen: true,
        modalTitle: t('MERCHANT.CANCEL_MODAL_TITLE'),
        modalText: t('MERCHANT.CANCEL_MODAL_TEXT'),
        confirmButtonText: t('MERCHANT.CANCEL_MODAL_CONFIRM_BTN_NAME'),
        onConfirm: closeModal,
        onCancel: (): void => {
          closeModal();
          setIsEditMode(false);
          formik.setValues(initialValues);
	        setUserRole(user?.group[0]);
        }
      });
		} else {
			setIsEditMode(false);
		}
	};

	return (
		<>
			<SectionWrapper
				title={t('USER.USER_INFO')}
				sectionAdditionalClass="mb-5 min-height-200"
				isEditMode={isEditMode}
				setIsEditMode={setIsEditMode}
				onEditClick={handleSaveClick}
				onCancelClick={handleCancelClick}
				loading={isLoading}
				isCardBody
				isEditBlock
			>
				<div className="row">
					{
						fields.map(item => (
							<FormInput
								{...item}
								key={item.key}
								formik={formik}
								isEditMode={isEditMode}
							/>
						))
					}
					<div className="col-6 col-sm-4 col-md-3 mt-10">
						<Input
							formik={formik}
							fieldLabel={FORM_FIELDS_NAMES.IS_MASKED_PII}
							checkboxFieldName={FORM_FIELDS.IS_MASKED_PII}
							inputType="checkbox"
							additionalClass="cursor-pointer"
							disabled={!isEditMode}
						/>
					</div>
				</div>

				<div className="separator" />

				{
					!checkRoles({ userData: { userGroups: user?.group } as UserModel }, CLAIMS.SUPER_ADMIN) && (
						<div className=" ">
							<div className="fs-2 py-5 mb-3 border-bottom-separator">
								Roles
							</div>
							<div className="d-flex flex-column align-items-start justify-content-start">
								{
									ROLES.map((role, index) => (
										<RadioInput
											key={`role-${index}`}
											{...role}
											currentValue={userRole}
											setCurrentValue={setUserRole}
											isDisabled={!isEditMode}
										/>
									))
								}
							</div>
						</div>
					)
				}

			</SectionWrapper>

			<ModalWindow
				{...modalState}
				onClose={closeModal}
			/>

		</>
	);
};
